import { oc } from '@orpc/contract'
import { z } from 'zod'

export const notificationCategorySchema = z.enum(['notification', 'activity'])

export const notificationTypeSchema = z.enum([
  'task_assigned',
  'task_comment',
  'chat_message',
  'document_updated',
  'document_comment',
  /** Mentioned with an @-chip in a document (board 9s8r); `anchor` is the block id. */
  'document_mention',
  /** Mentioned in a document comment (board 9p2d); `anchor` is the thread id. */
  'comment_mention',
  'drawing_updated',
  'inbox_email',
  'inbox_run_failed',
])

export const notificationResourceTypeSchema = z.enum([
  'task',
  'chat_thread',
  'document',
  'drawing',
  'inbox_email',
])

export const notificationSchema = z.object({
  id: z.string(),
  category: notificationCategorySchema,
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string().nullable(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  projectId: z.string(),
  projectName: z.string().nullable(),
  resourceType: notificationResourceTypeSchema.nullable(),
  resourceId: z.string().nullable(),
  /** Position inside the resource (a document block id → `#<blockId>`). */
  anchor: z.string().nullable(),
  isRead: z.boolean(),
  /** First creation of the row. */
  createdAt: z.string(),
  /**
   * When the row last became news: its creation, or the latest event the
   * engine coalesced into it (re-mention, new reply). The list is ordered by
   * it and the panel shows it — never bumped by mark-read (board card 7u11).
   */
  notifiedAt: z.string(),
})

export type Notification = z.infer<typeof notificationSchema>
export type NotificationCategory = z.infer<typeof notificationCategorySchema>
export type NotificationType = z.infer<typeof notificationTypeSchema>
export type NotificationResourceType = z.infer<typeof notificationResourceTypeSchema>

// --- Procedure contracts ---

/** Newest first by `notifiedAt`; `cursor` is opaque (the last item's position). */
export const notificationsListContract = oc
  .input(
    z.object({
      cursor: z.string().optional(),
      limit: z.number().int().min(1).max(100).optional(),
      category: notificationCategorySchema.optional(),
    })
  )
  .output(
    z.object({
      items: z.array(notificationSchema),
      nextCursor: z.string().nullable(),
    })
  )

export const notificationsBadgeContract = oc.input(z.object({})).output(
  z.object({
    unreadCount: z.number().int(),
    hasUnseenActivity: z.boolean(),
  })
)

export const notificationsMarkReadContract = oc
  .input(
    z.object({
      notificationId: z.string().min(1),
    })
  )
  .output(
    z.object({
      success: z.boolean(),
    })
  )

export const notificationsMarkAllReadContract = oc
  .input(
    z.object({
      category: notificationCategorySchema.optional(),
    })
  )
  .output(
    z.object({
      success: z.boolean(),
    })
  )

export const notificationsRegisterPushContract = oc
  .input(
    z.object({
      endpoint: z.string().url(),
      p256dhKey: z.string().min(1),
      authKey: z.string().min(1),
    })
  )
  .output(
    z.object({
      success: z.boolean(),
    })
  )

export const notificationsUnregisterPushContract = oc
  .input(
    z.object({
      endpoint: z.string().url(),
    })
  )
  .output(
    z.object({
      success: z.boolean(),
    })
  )

/** Send a test push to every subscription of the caller (settings page). */
export const notificationsTestPushContract = oc.input(z.object({})).output(
  z.object({
    /** False when the server has no VAPID keys — push is not configured. */
    configured: z.boolean(),
    subscriptions: z.number().int(),
    sent: z.number().int(),
    failed: z.number().int(),
  })
)
